/**
 * Authentication service for managing JWT tokens with automatic refresh
 */

import { API_BASE } from '../config';

/**
 * Decode JWT token payload (without verification - only for client-side use)
 * @param token JWT token string
 * @returns Decoded payload object or null if invalid
 */
function decodeJWT(token: string): any {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }
    
    // Decode base64url payload (second part)
    const payload = parts[1];
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Failed to decode JWT:', error);
    return null;
  }
}

/**
 * Get the user role from the JWT access token
 * @returns User role ('sales' or 'surveyor') or null if not found
 */
export function getUserRole(): 'sales' | 'surveyor' | null {
  const tokens = getTokens();
  if (!tokens) {
    return null;
  }
  
  const payload = decodeJWT(tokens.accessToken);
  if (!payload) {
    return null;
  }
  
  // Extract role claim - adjust claim name if needed based on your JWT structure
  const role = payload.role || payload.Role;
  
  if (role === 'sales' || role === 'surveyor') {
    return role;
  }
  
  console.warn('Unknown user role in JWT:', role);
  return null;
}

export function getName(): string | null {
  const tokens = getTokens();
  if (!tokens) {
    return null;
  }
  
  const payload = decodeJWT(tokens.accessToken);
  if (!payload) {
    return null;
  }
  
  // Extract role claim - adjust claim name if needed based on your JWT structure
  const name = payload.FullName || "Unknown";
  
  return name;
}

const TOKEN_STORAGE_KEY = 'auth_tokens';
const REFRESH_BUFFER_MS = 60000; // Refresh 1 minute before expiry

export interface TokenData {
  accessToken: string;
  expiresIn: number; 
  expiresAt?: number;
}

/**
 * Store tokens in localStorage with calculated expiry time
 */
export function setTokens(tokenData: TokenData): void {
  const expiresAt = Date.now() + (tokenData.expiresIn * 1000);

  const fullTokenData: TokenData = {
    ...tokenData,
    expiresAt
  };

  localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(fullTokenData));
  //localStorage.setItem('jwt', tokenData.accessToken); // legacy
}

/**
 * Get stored tokens from localStorage
 */
export function getTokens(): TokenData | null {
  const stored = localStorage.getItem(TOKEN_STORAGE_KEY);
  if (!stored) return null;
  
  try {
    return JSON.parse(stored) as TokenData;
  } catch (error) {
    console.error('Failed to parse stored tokens:', error);
    return null;
  }
}

/**
 * Check if user is authenticated (has valid tokens)
 */
export function isAuthenticated(): boolean {
  const tokens = getTokens();
  return tokens !== null && tokens.accessToken !== '';
}

/**
 * Clear all stored tokens (logout)
 */
export function clearTokens(): void {
  localStorage.removeItem(TOKEN_STORAGE_KEY);
  localStorage.removeItem('jwt'); // Clear legacy key too
}

/**
 * Check if the current access token is expired or about to expire
 */
function isTokenExpiring(tokens: TokenData): boolean {
  if (!tokens.expiresAt) return true;
  
  const timeUntilExpiry = tokens.expiresAt - Date.now();
  return timeUntilExpiry <= REFRESH_BUFFER_MS;
}

/**
 * Refresh the access token using the refresh token
 */
async function refreshAccessToken(): Promise<TokenData> {
  const response = await fetch(`${API_BASE}/auth/refresh`, {
    method: 'POST',
    credentials: 'include',   //
    headers: {}               //
  });

  if (!response.ok) {
    throw new Error('Token refresh failed');
  }

  const data = await response.json();

  if (!data.accessToken) {
    throw new Error('Invalid token response');
  }

  const newTokenData: TokenData = {
    accessToken: data.accessToken,
    expiresIn: data.expiresIn || 900
  };

  setTokens(newTokenData);
  return newTokenData;
}

/**
 * Get a valid access token, refreshing if necessary
 * This is the main method to use before making API calls
 */
export async function getValidToken(): Promise<string> {
  const tokens = getTokens();
  
  if (!tokens) {
    throw new Error('No authentication tokens found');
  }
  
  // If token is not expiring, return current access token
  if (!isTokenExpiring(tokens)) {
    return tokens.accessToken;
  }
  
  // Token is expiring or expired, try to refresh
  try {
    const newTokens = await refreshAccessToken();
    return newTokens.accessToken;
  } catch (error) {
    // Refresh failed, clear tokens and throw error
    clearTokens();
    throw new Error('Authentication expired. Please login again.');
  }
}

/**
 * Handle authentication errors from API calls
 * Returns true if the error was handled (token refreshed, should retry)
 * Returns false if the error cannot be handled (user needs to login)
 */
export async function handleAuthError(response: Response): Promise<boolean> {
  if (response.status !== 401) {
    return false; // Not an auth error
  }
  
  const tokens = getTokens();
  if (!tokens) {
    return false; // No tokens to refresh
  }
  
  try {
    // Try to refresh the token
    await refreshAccessToken();
    return true; // Token refreshed, caller should retry the request
  } catch (error) {
    // Refresh failed, clear tokens
    clearTokens();
    return false; // Cannot recover, user needs to login
  }
}

/**
 * Login and store tokens
 */
export async function login(username: string, password: string): Promise<TokenData> {
  const response = await fetch(`${API_BASE}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ 
      Username: username, 
      Password: password 
    }),
  });
  
  if (!response.ok) {
    throw new Error('Login failed');
  }
  
  const data = await response.json();
  
  if (!data.accessToken) {
    throw new Error('Invalid login response');
  }
  
  const tokenData: TokenData = {
    accessToken: data.accessToken,
    expiresIn: data.expiresIn || 900
  };
  
  setTokens(tokenData);
  return tokenData;
}

/**
 * Logout - clear tokens and optionally notify server
 */
export async function logout(notifyServer: boolean = false): Promise<void> {
  if (notifyServer) {
    try {
      const token = await getValidToken();
      await fetch(`${API_BASE}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
    } catch (error) {
      console.error('Logout notification failed:', error);
      // Continue with local logout even if server notification fails
    }
  }
  
  clearTokens();
}
